<div class="card">
  <h2 class="card-title">Scholarship awards</h2>

  <div class="card-body">
    <form [formGroup]="filters" class="toolbar" (ngSubmit)="load()">
      <label>
        Cycle
        <input type="text" formControlName="cycle" placeholder="e.g. 2026" />
      </label>

      <label>
        Scholarship
        <select formControlName="scholarshipId">
          <option [ngValue]="null">All scholarships</option>
          @for (scholarship of scholarships(); track scholarship.id) {
            <option [ngValue]="scholarship.id">{{ scholarship.title }}</option>
          }
        </select>
      </label>

      <label>
        Status
        <select formControlName="status">
          <option value="">All statuses</option>
          @for (status of statuses; track status) {
            <option [value]="status">{{ status }}</option>
          }
        </select>
      </label>

      <label>
        Search
        <input type="text" formControlName="search" placeholder="Student, award or scholarship" />
      </label>

      <div class="toolbar-actions">
        <button type="submit" class="btn btn-primary btn-sm"><i class="fas fa-search"></i> Filter</button>
      </div>
    </form>

    @if (error()) {
      <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ error() }}</div>
    }
    @if (message()) {
      <div class="alert alert-success"><i class="fas fa-check-circle"></i> {{ message() }}</div>
    }

    <!-- Award a whole cycle of approved applications --------------------------- -->
    @if (authorization.hasPermission(permissions.AwardManage)) {
      <div class="action-panel">
        <strong>Award every approved application for a cycle</strong>
        <p class="field-hint">
          Approved applications are awarded automatically when they are approved. Use this to
          back-fill a cycle, or preview what would be created.
        </p>

        <form [formGroup]="cycleForm" class="inline-form" (ngSubmit)="grantCycle(false)">
          <label>
            Cycle
            <input type="text" formControlName="cycle" />
          </label>
          <label>
            Starts
            <input type="date" formControlName="startDate" />
          </label>
          <label>
            Ends
            <input type="date" formControlName="endDate" />
          </label>
          <label>
            Scholarship
            <select formControlName="scholarshipId">
              <option [ngValue]="null">All scholarships</option>
              @for (scholarship of scholarships(); track scholarship.id) {
                <option [ngValue]="scholarship.id">{{ scholarship.title }}</option>
              }
            </select>
          </label>

          <button type="button" class="btn btn-secondary btn-sm" (click)="grantCycle(true)" [disabled]="busy()">
            Preview
          </button>
          <button type="submit" class="btn btn-primary btn-sm" [disabled]="busy()">
            {{ busy() ? 'Working…' : 'Award cycle' }}
          </button>
        </form>

        @if (cycleResult(); as result) {
          <div class="result-note">
            <span>
              Considered {{ result.considered }} · awarded {{ result.awarded }} · skipped {{ result.skipped }}
              @if (result.dryRun) { (preview only) }
            </span>
            @for (skip of result.skips; track skip.scholarshipApplicationId) {
              <small>
                {{ skip.applicationReferenceNumber || skip.scholarshipApplicationId }}
                @if (skip.studentName) { — {{ skip.studentName }} }: {{ skip.reason }}
              </small>
            }
          </div>
        }
      </div>
    }

    <div class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th>Award</th>
            <th>Student</th>
            <th>Scholarship</th>
            <th>Cycle</th>
            <th>Period</th>
            <th>Allocated</th>
            <th>Used</th>
            <th>Remaining</th>
            <th>Months left</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="11" class="empty-cell"><i class="fas fa-spinner fa-spin"></i> Loading awards…</td></tr>
          }
          @if (!loading() && awards().length === 0) {
            <tr><td colspan="11" class="empty-cell">No awards match these filters.</td></tr>
          }
          @for (award of awards(); track award.id) {
            <tr>
              <td>{{ award.referenceNumber }}</td>
              <td>
                {{ award.student.fullName || '—' }}
                @if (award.student.emailAddress) { <small>{{ award.student.emailAddress }}</small> }
              </td>
              <td>{{ award.scholarshipTitle }}</td>
              <td>{{ award.cycle }}</td>
              <td>{{ award.startDate | date: 'mediumDate' }} — {{ award.endDate | date: 'mediumDate' }}</td>
              <td>{{ money(award.totalAllocation, award.currency) }}</td>
              <td>{{ money(award.usedAmount, award.currency) }}</td>
              <td>{{ money(award.remainingAmount, award.currency) }}</td>
              <td>{{ award.monthsRemaining }}</td>
              <td><span [class]="statusClass(award.status)">{{ award.status }}</span></td>
              <td>
                <button type="button" class="btn btn-secondary btn-sm" (click)="open(award)">Open</button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>


<!-- Award detail --------------------------------------------------------- -->
@if (selected(); as award) {
  <div class="modal-backdrop" (click)="close()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Award {{ award.referenceNumber }}</h2>
        <button class="modal-close" (click)="close()"><i class="fas fa-times"></i></button>
      </div>

      <div class="modal-body">
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Status</span>
            <span class="detail-value"><span [class]="statusClass(award.status)">{{ award.status }}</span></span>
          </div>
          <div class="detail-item"><span class="detail-label">Student</span><span class="detail-value">{{ award.student.fullName || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Scholarship</span><span class="detail-value">{{ award.scholarshipTitle }}</span></div>
          <div class="detail-item"><span class="detail-label">Cycle</span><span class="detail-value">{{ award.cycle }}</span></div>
          <div class="detail-item"><span class="detail-label">Period</span>
            <span class="detail-value">{{ award.startDate | date: 'mediumDate' }} — {{ award.endDate | date: 'mediumDate' }}</span>
          </div>
          <div class="detail-item"><span class="detail-label">Allocated</span><span class="detail-value">{{ money(award.totalAllocation, award.currency) }}</span></div>
          <div class="detail-item"><span class="detail-label">Used</span><span class="detail-value">{{ money(award.usedAmount, award.currency) }}</span></div>
          <div class="detail-item"><span class="detail-label">Remaining</span><span class="detail-value">{{ money(award.remainingAmount, award.currency) }}</span></div>
          <div class="detail-item"><span class="detail-label">Duration</span>
            <span class="detail-value">{{ award.monthsElapsed }} of {{ award.monthsElapsed + award.monthsRemaining }} months</span>
          </div>
        </div>

        <h3 class="section-heading">Budget usage</h3>
        <div class="progress-track">
          <div class="progress-fill" [style.width]="progressWidth(award.budgetProgressPercent)"></div>
        </div>
        <p class="detail-note">{{ award.budgetProgressPercent }}% of the allocation has been used.</p>

        <h3 class="section-heading">Benefit allocations</h3>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Benefit</th>
                <th>Recurring</th>
                <th>Paid to provider</th>
                <th>Allocated</th>
                <th>Used</th>
                <th>Remaining</th>
                <th>Claims</th>
                <th>Payments</th>
              </tr>
            </thead>
            <tbody>
              @if (award.benefits.length === 0) {
                <tr><td colspan="8" class="empty-cell">No benefit allocations on this award.</td></tr>
              }
              @for (benefit of award.benefits; track benefit.id) {
                <tr>
                  <td>{{ benefit.benefitName }}</td>
                  <td>{{ benefit.isRecurring ? 'Yes' : 'No' }}</td>
                  <td>
                    {{ benefit.payDirectToProvider ? 'Yes' : 'No' }}
                    @if (benefit.providerName) { <small>{{ benefit.providerName }}</small> }
                  </td>
                  <td>{{ money(benefit.allocatedAmount, award.currency) }}</td>
                  <td>{{ money(benefit.usedAmount, award.currency) }}</td>
                  <td>{{ money(benefit.remainingAmount, award.currency) }}</td>
                  <td>{{ benefit.claimCount }}</td>
                  <td>{{ benefit.paymentCount }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (authorization.hasPermission(permissions.AwardManage) && award.status !== 'TERMINATED') {
          <h3 class="section-heading">Change status</h3>
          <form [formGroup]="statusForm" class="form-grid">
            <label>
              New status
              <select formControlName="status">
                @for (status of statuses; track status) {
                  <option [value]="status">{{ status }}</option>
                }
              </select>
            </label>
            <label>
              Reason (required to complete or terminate)
              <input type="text" formControlName="reason" />
            </label>
          </form>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="close()">Close</button>
        @if (authorization.hasPermission(permissions.AwardManage) && award.status !== 'TERMINATED') {
          <button type="button" class="btn btn-primary" (click)="changeStatus()" [disabled]="busy()">
            {{ busy() ? 'Saving…' : 'Apply status change' }}
          </button>
        }
      </div>
    </div>
  </div>
}
